/**
 * CLAB wishlist — one board for everything worth keeping.
 *
 * A saved thing is not only a product. A campaign frame from the social reel,
 * a look, a picture from any section: they all land on the same board, which
 * is what makes it read like a pinboard rather than a "saved products" list.
 * So an item is just:
 *
 *   { id, kind: 'product' | 'image', image, title, meta, url, added }
 *
 * `id` is whatever the trigger says it is — a product handle, or a section id
 * plus the block's index — and it is the only thing that decides whether two
 * saves are the same save.
 *
 * Any element can be a trigger:
 *
 *   <button data-wish='{"id":"...","kind":"image","image":"…"}'>
 *
 * The click is caught here, on the document, so triggers can arrive with a
 * section render, a modal, or a board that draws itself — nothing has to bind
 * anything. Triggers reflect their state through `aria-pressed`, which is what
 * the CSS keys off, so the markup never carries a duplicate class.
 *
 * Storage is localStorage under one key. A board that vanishes on a browser
 * clear is the accepted cost of not asking anyone to make an account.
 */
(function () {
  var KEY = 'clab-wishlist';
  var EVENT = 'clab:wishlist';

  var read = function () {
    try {
      var raw = localStorage.getItem(KEY);
      var list = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(list)) return [];
      // Anything that is not an item with an id gets dropped on the way out:
      // an older build of the product page wrote bare handles in here, and a
      // board that tries to draw a string throws on the first field it reads.
      return list.filter(function (x) {
        return x && typeof x === 'object' && x.id;
      });
    } catch (e) {
      return [];
    }
  };

  var write = function (list) {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {
      /* private mode, or the quota is gone: the board still works for this
         page view, it just will not survive the reload. */
    }
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { items: list } }));
  };

  var api = {
    all: function () {
      return read();
    },
    has: function (id) {
      return read().some(function (x) {
        return x.id === id;
      });
    },
    add: function (item) {
      if (!item || !item.id) return read();
      var list = read();
      if (list.some(function (x) { return x.id === item.id; })) return list;
      // Newest first: a board reads as a stack of what you just found.
      list.unshift(Object.assign({ added: Date.now() }, item));
      write(list);
      return list;
    },
    remove: function (id) {
      var list = read().filter(function (x) {
        return x.id !== id;
      });
      write(list);
      return list;
    },
    toggle: function (item) {
      return api.has(item.id) ? (api.remove(item.id), false) : (api.add(item), true);
    },
    clear: function () {
      write([]);
    }
  };

  window.clabWishlist = api;

  var parseItem = function (el) {
    var raw = el.getAttribute('data-wish');
    if (!raw) return null;
    // Two forms: a bare id, or the whole item as JSON.
    if (raw.charAt(0) !== '{') return { id: raw, kind: el.dataset.wishKind || 'product' };
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  };

  var paint = function () {
    var list = read();
    var ids = list.map(function (x) { return x.id; });
    document.querySelectorAll('[data-wish]').forEach(function (el) {
      var item = parseItem(el);
      if (!item) return;
      el.setAttribute('aria-pressed', String(ids.indexOf(item.id) > -1));
    });
    document.querySelectorAll('[data-wish-count]').forEach(function (el) {
      el.textContent = list.length;
    });
    // The dot is the visible half: it says "there is something in here" and
    // nothing else, which is all an icon this size can carry.
    document.querySelectorAll('[data-wish-dot]').forEach(function (el) {
      el.hidden = list.length === 0;
    });
    // The header's heart says it by filling instead: the bag keeps the only
    // dot in the bar.
    document.querySelectorAll('[data-wish-fill]').forEach(function (el) {
      el.classList.toggle('is-filled', list.length > 0);
    });
  };

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-wish]');
    if (!trigger) return;
    var item = parseItem(trigger);
    if (!item) return;
    // Triggers sit inside links and cards often enough that this has to be
    // unconditional: saving is never also a navigation.
    e.preventDefault();
    e.stopPropagation();
    api.toggle(item);
    trigger.setAttribute('aria-pressed', String(api.has(item.id)));
    // The pop is the whole feedback; it runs on the trigger, not on a toast.
    trigger.classList.remove('is-popping');
    void trigger.offsetWidth;
    trigger.classList.add('is-popping');
  });

  document.addEventListener(EVENT, paint);
  // Another tab is the same board.
  window.addEventListener('storage', function (e) {
    if (e.key === KEY) paint();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', paint);
  } else {
    paint();
  }
})();
